/**
 * Domain Layer - Redux Store
 * Centralized state management configuration
 */

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector, type TypedUseSelectorHook } from "react-redux";
import { siteConfig } from "@/config/site";
import uiReducer from "./slices/ui-slice";
import { dashboardReducer } from "@/features/dashboard/domain/store";

/**
 * Root reducer combining all slice reducers
 */
const rootReducer = combineReducers({
  ui: uiReducer,
  dashboard: dashboardReducer,
  // Add more reducers here as needed
});

/**
 * Configured Redux store
 */
export const store = configureStore({
  reducer: rootReducer,
  devTools: !siteConfig.isProduction,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["auth/refreshToken/fulfilled"],
        ignoredPaths: ["auth.user"],
      },
    }),
});

/**
 * Store types
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/**
 * Typed hooks for Redux store
 * Use these instead of plain `useDispatch` and `useSelector`
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
